import React, { useState, useRef, useEffect } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ItemActions, TooltipedIcon } from '@europrocurement/flexy-components';
import { demoIcon } from '@europrocurement/l2d-icons';

interface HeaderTitleProps {
    clientOpereRaisonSociale: string;
    prescriberId?: number;
    sousClientId?: number;
    onEdit?: () => void;
    demo?: boolean;
}

export const HeaderTitle: React.FunctionComponent<HeaderTitleProps> = function ({
    clientOpereRaisonSociale,
    prescriberId,
    sousClientId,
    onEdit,
    demo = false,
}) {
    const [titleHovered, setTitleHovered] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const titleRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const el = titleRef.current;
        if (!el) return undefined;

        const checkTruncation = () => setIsTruncated(el.scrollWidth > el.clientWidth);
        checkTruncation();

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
    }, []);

    const titleWrapperStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '0 8px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginBottom: '0',
    };

    const titleStyle = {
        overflow: 'hidden',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
        minWidth: 0,
    };

    const prescriberIsClient = prescriberId !== undefined && prescriberId === sousClientId;
    const title = (
        <Tooltip
            title={clientOpereRaisonSociale}
            disableHoverListener={!isTruncated}
            disableFocusListener={!isTruncated}
            disableTouchListener={!isTruncated}
            enterTouchDelay={0}
            leaveTouchDelay={1500}
        >
            <Typography
                ref={titleRef}
                variant="l2dh5"
                sx={titleStyle}
            >
                {clientOpereRaisonSociale}
            </Typography>
        </Tooltip>
    );

    return (
        <Box sx={titleWrapperStyle}>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '24px',
                    minWidth: 0,
                }}
                onMouseEnter={() => setTitleHovered(true)}
                onMouseLeave={() => setTitleHovered(false)}
            >
                {demo && (
                    <TooltipedIcon
                        icon={demoIcon}
                        tooltip="Démo"
                        size="xs"
                        color="error"
                    />
                )}
                {prescriberIsClient && (
                    <Tooltip title="Pour modifier le prescripteur, rendez vous sur sa fiche">
                        <Typography
                            variant="l2dh5"
                            sx={titleStyle}
                        >
                            {clientOpereRaisonSociale}
                        </Typography>
                    </Tooltip>
                )}
                {!prescriberIsClient && (
                    <>
                        {title}
                        <ItemActions
                            onEdit={onEdit}
                            editTooltip="Éditer la société opérée"
                            visible={isTruncated || titleHovered}
                            slideDirection="left"
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};
